import getProductVariant from './utils/getProductVariant';
const {
  mergeCustomAttendanceCodes,
  getCustomAttendanceCodes,
  getCustomAttendanceCodesForDate,
  getAttendanceAcademicYear,
  getAttendanceCodeDateKey,
  getAttendanceCodeChangeDates,
  parseAttendanceMarks,
  getAttendanceSessions,
} = require('./utils/customAttendanceCodes.cjs');

export const publicRoutes = [
  '/login',
  '/logout',
  '/account/reset',
  '/account/forgot',
  '/account/confirm',
];

export const passwordPolicyInfo =
  'Passwords must be at least 8 characters and should include at least 1 number and 1 uppercase letter.';

export const region = 'eu-west-2';
export const userPoolId = 'eu-west-2_KzmXKZCGO';
export const userPoolWebClientId = '7lb64r2dbb980fb5j8dnlog5kl';

export const yearGroups = [
  { name: 'Nu', scottishName: null },
  { name: 'EYFS', scottishName: 'Nursery' },
  { name: 'Y1', scottishName: 'P1' },
  { name: 'Y2', scottishName: 'P2' },
  { name: 'Y3', scottishName: 'P3' },
  { name: 'Y4', scottishName: 'P4' },
  { name: 'Y5', scottishName: 'P5' },
  { name: 'Y6', scottishName: 'P6' },
  { name: 'Y7', scottishName: 'P7' },
  { name: 'Y8', scottishName: 'S1' },
  { name: 'Y9', scottishName: 'S2' },
  { name: 'Y10', scottishName: 'S3' },
  { name: 'Y11', scottishName: 'S4' },
  { name: 'Y12', scottishName: 'S5' },
  { name: 'Y13', scottishName: 'S6' },
  { name: 'Y14', scottishName: null },
];

export const variants = {
  SMIDPRIMARY: {
    title: 'SMID Primary',
    url: 'https://dashboard.smidreport.com',
    fromAddress: 'primarysupport@smidreport.com',
  },
  TRACIWR: {
    title: 'Traciwr',
    url: 'https://dashboard.traciwr.com',
    fromAddress: 'support@traciwr.com',
  },
  TLEVELTRACKER: {
    title: 'T Level Tracker',
    url: 'https://dashboard.tleveltracker.com',
    fromAddress: 'support@tleveltracker.com',
  },
  EDRIAN: {
    title: 'Edrian',
    url: 'https://dashboard.edrian.co.uk',
    fromAddress: 'support@edrian.co.uk',
  },
};

export const defaultVariant = variants['TRACIWR'];

export const defaultErrorMessage =
  'Something went wrong, please try again later.';

export const defaultScoreSystems = [
  {
    name: 'Age (Year & Month)',
    options: Array.from(
      {
        length: 16,
      },
      (_, year) => {
        return Array.from(
          {
            length: 12,
          },
          (_, month) => {
            return {
              name: `${year} year${year !== 1 ? 's' : ''} ${month} month${
                month !== 1 ? 's' : ''
              }`,
              alternatives: [
                `${year}y${month}m`,
                `${year}.${
                  month > 0 ? month.toString().padStart(2, '0') : month
                }`,
                ...(month == 0 ? [year.toString()] : []),
              ],
            };
          }
        );
      }
    )
      .flat()
      .splice(1) // remove 0 years, 0 months
      .map((opt, index) => ({
        name: opt.name,
        precedence: index + 1,
        alternatives: opt.alternatives,
      })),
  },
  {
    name: 'Age Standardised Score',
    isFreeEntry: true,
    min: 10,
    max: 145,
    ranges: [
      { start: 65, end: 80, color: '#eb144c' },
      { start: 80, end: 95, color: '#ff6900' },
      { start: 95, end: 110, color: '#fcb900' },
      { start: 110, end: 125, color: '#0693e3' },
      { start: 125, end: 142, color: '#00d084' },
    ],
  },
];

export const attendanceSystem = {
  id: 'ATTENDANCE',
  name: 'Attendance Tile',
  isFreeEntry: true,
  min: 0,
  max: 100,
  options: [],
  ranges: [
    { start: 0, end: 83, color: '#a80000' },
    { start: 83, end: 94, color: '#e67039' },
    { start: 94, end: 100, color: '#00ff00' },
  ],
  comparisonRanges: [],
};

export const languageResources = {
  en: {
    translation: {
      key: 'test',
    },
  },
  cy: {
    translation: {
      // ['Manage Aspects']: 'Manage Aspects Welsh',
      ['Home']: 'Cartref',
      ['Manage Users']: 'Rheoli Defnyddwyr',
      ['Data Captures']: 'Capsiynau Data',
      ['Help & Support']: 'Cymorth a Chymorth Technegol',
      ['Settings']: 'Gosodiadau',
      ['Log Out']: 'Allgofnodi',
      ['Welcome to']: 'Croeso I (school name as it is)',
      ['Quick Actions']: 'Camau Cyflym',
      ['Manage Aspects']: 'Rheoli Agweddau',
      ['Manage Student Groups']: 'Rheoli Grwpiau Myfyrwyr',
      ['Score Systems']: 'Sistemau Sgorio',
      ['Data Captures']: 'Capsiynau Data',
      ['Manage Students']: ' Rheoli Myfyrwyr',
      ['Template View']: 'Golwg Templed',
      ['Import Results']: 'Mewnforio Canlyniadau',
      ['Your Notifications']: 'Eich Hysbysiadau',
      ["Your school hasn't sent any notifications"]:
        'Nid yw eich ysgol wedi anfon unrhyw hysbysiadau',
      ['When they do, those notifications will show up here.']:
        "Pan fyddan nhw'n wneud hynny, bydd y hysbysiadau hynny'n ymddangos yma.",
      ['Manage Aspects']: 'Rheoli Agweddau',
      ['Search']: 'Chwilio',
      ['New Aspect']: 'Agwedd Newydd',
      ['Aspect']: 'Agwedd',
      ['Descriptor']: 'Disgrifydd',
      ['Linked Classes']: 'Dosbarthiadau Cysylltiedig',
      ['Associated Staff']: 'Staff Cysylltiedig',
      ['No. Results']: 'Dim Canlyniadau',

      ['School Groups']: 'Grwpiau Ysgol',
      ['Form Groups']: 'Grwpiau Ffurf',
      ['Year Groups']: 'Grwpiau Blwyddyn',
      ['Import Groups']: 'Mewnforio Grwpiau',
      ['Import Student-Group Links']: 'Mewnforio Dolenni Myfyriwr-Grŵp',
      ['Need help getting started?']: 'Angen help i ddechrau?',
      ['Download import group CSV file example']:
        'Llwytho i lawr enghraifft o ffeil CSV ',
      ['Download import group links CSV file example']:
        'Llwytho i lawr enghraifft o ffeil ',
      ['Manage Student Groups']: 'Rheoli Grwpiau Myfyrwyr',
      ['Student Group']: 'Grŵp Myfyrwyr',
      ['Search']: 'Chwilio',
      ['New Group']: 'Grŵp Newydd',
      ['Linked students']: 'Myfyrwyr cysylltiedig',
      ['Students']: 'Myfyrwy',

      ['Manage Score Systems']: 'Rheoli Systemau Sgorio',
      ['Search']: 'Chwilio',
      ['New System']: 'System Newydd',
      ['System Name']: "Enw'r System",
      ['Allow custom input?']: 'Caniatáu mewnbwn cysefin',
      ['Available options']: 'Opsiynau ar gael',
      ['No. Results']: 'Dim Canlyniada',

      ['Manage Captures']: 'Rheoli Capsiynau',
      ['Search']: 'Chwilio',
      ['New Capture']: 'Capsiynu Newydd',
      ['Export All']: "Allforio'r Cyfan",
      ['Capture title']: 'Teitl y Capsiynu',
      ['Start date']: 'Dyddiad dechrau',
      ['End date']: 'Dyddiad gorffen',
      ['Aspects & Score Systems']: 'Agweddau a Systemau Sgori',

      ['Upload Students']: 'Llwytho Myfyrwyr i Fyny',
      ['Upload Single Student']: 'Llwytho Myfyriwr Unigol i Fyny',
      ['Use the select CSV button for multiple students']:
        'Defnyddiwch y botwm dewis ',
      ['Select CSV']: 'Dewiswch CSV',
      ['Need help getting started?']: 'Angen help i ddechrau?',
      ['Download a sample CSV file']: 'Llwytho i lawr ffeil CSV arallgopi',
      ['Previously Uploaded Students']: 'Myfyrwyr a Lwythwyd Ymlaen Gynt',
      ['Search']: 'Chwilio',
      ['Forename']: 'Enw Cyntaf',
      ['Surname']: 'Cyfenw',
      ['Gender']: 'Rhyw',
      ['Admission Number']: 'Rhif Derbyn',
      ['Year']: 'Blwyddyn',
      ['Form Group']: 'Grŵp Ffurflen',
      ['Dashboard']: 'Panel Rheolaet',

      ['Use Template']: 'Defnyddio Templed',
      ['You must select 1 capture to view data for this template']:
        'Rhaid i chi ddewis 1',
      ['Select a template']: 'Dewis temple',

      ['Manage Users']: 'Rheoli Defnyddwyr',
      ['Search']: 'Chwilio',
      ['Title']: 'Teitl',
      ['Forename']: 'Enw Cyntaf',
      ['Surname']: 'Cyfenw',
      ['Account type']: 'Math y Cyfrif',
      ['Staff code']: 'Côd Staff',
      ['Email']: 'E-bost',
      ['Activated?']: "Wedi'i actifadu",
      ['Last login']: 'Mewngofnodi diwetha',

      ['Secure Upload']: 'Llwytho i fyny yn ddiogel',
      ['User Guide']: 'Cymorth Defnyddiwr',
      ['Contact Us']: 'Cysylltwch â Ni',
      ['Send the data to us using our secure upload below and we will handle the process for you!']:
        "Gan anfon y data atom ni gan ddefnyddio ein llwytho i fyny diogel isod, a byddwn yn delio â'r broses ar eich rhan chi",
      ['Take a look at our user guide for everything you need to know about getting  started with Traciwr!']:
        'Edrychwch ar ein canllaw defnyddiwr am bopeth y mae angen i chi ei wybod am ddechrau gyda Traciwr',
      [`If you have any questions, please send an email to support@traciwr.com and we'll be happy to help.`]:
        "Os oes gennych unrhyw gwestiynau, anfonwch e-bost at support@traciwr.com a byddwn yn hapus i'ch helpu.",
      ['Select File']: 'Dewis Ffeil',
      ['Download User Guide']: 'Llwytho i Lawr Canllaw Defnyddiwr',
      ['Support Files']: 'Ffeiliau Cymorth',
      ['Search']: 'Chwilio',
      ['Filename']: "Enw'r Ffeil",
      ['Upload Date']: 'Dyddiad Llwytho i Fyn',
      ['Tutorials']: 'Tiwtorialau',

      ['Back To Templates']: "Yn ôl i'r templedi",
      ['Export Report']: "Allforio'r adroddiad",
      ['Preparing PDF...']: "Yn paratoi'r PDF...",
      ['AOLE content language']: 'Iaith cynnwys AOLE',
      ['English']: 'Saesneg',
      ['Welsh']: 'Cymraeg',
      ['Changes AOLE figures and exports']:
        'Yn newid ffigurau ac allforion AOLE',
      ['Apply filters']: 'Gosod hidlyddion',
      ['Active filters']: 'Hidlyddion gweithredol',
      ['Reset all']: 'Ailosod popeth',
      ['School groups']: 'Grwpiau ysgol',
      ['Year groups']: 'Grwpiau blwyddyn',
      ['Form groups']: 'Grwpiau ffurf',
      ['Classes']: 'Dosbarthiadau',
      ['Included']: "Wedi'i gynnwys",
      ['Excluded']: "Wedi'i eithrio",
      ['Filter Value']: "Gwerth yr hidlydd",
      ['All']: 'Pob un',
      ['No options found']: 'Ni chanfuwyd unrhyw opsiynau',
      ['Export Reports']: 'Allforio adroddiadau',
      ['Include score system colour key page']:
        "Cynnwys tudalen allwedd lliw'r system sgorio",
      ['Export as one PDF file']: 'Allforio fel un ffeil PDF',
      ['Include Parent Response page']: 'Cynnwys tudalen ymateb rhieni',
      ['Cancel']: 'Canslo',
      ['Export']: 'Allforio',
      ['Failed to export report, please try again later.']:
        "Wedi methu allforio'r adroddiad. Rhowch gynnig arall arni yn nes ymlaen.",
      ['Exporting Reports']: 'Yn allforio adroddiadau',
      ['Students Selected']: "Myfyrwyr wedi'u dewis",
      ['Exports cannot exceed 100 students. Please use filters to limit selection.']:
        'Ni all allforion gynnwys mwy na 100 o fyfyrwyr. Defnyddiwch hidlyddion i gyfyngu ar y dewis.',
      ['Select student']: 'Dewiswch fyfyriwr',
      ['Add Comment (Optional)']: 'Ychwanegu sylw (dewisol)',
      ['Save Comment']: "Cadw'r sylw",
      ['Delete Comment']: "Dileu'r sylw",
      ['Preview Report']: "Rhagolwg o'r adroddiad",
      ['Start Date']: 'Dyddiad dechrau',
      ['End Date']: 'Dyddiad gorffen',
      ['Attendance']: 'Presenoldeb',
      ['Move this figure downward']: 'Symud y ffigur hwn i lawr',
      ['Move this figure upward']: 'Symud y ffigur hwn i fyny',
      ['Edit']: 'Golygu',
      ['Delete']: 'Dileu',
      ['Delete Figure']: "Dileu'r ffigur",
      ['Are you sure you wish to remove the figure']:
        "Ydych chi'n siŵr eich bod am dynnu'r ffigur?",
      ['Click here to create a new chart or table']:
        'Cliciwch yma i greu siart neu dabl newydd',
      ['Close']: 'Cau',
      ['Rows per page:']: 'Rhesi fesul tudalen:',
      ['rows']: 'rhesi',
      ['{from}-{to} of {count}']: '{from}-{to} o {count}',
      ['First Page']: 'Tudalen gyntaf',
      ['Previous Page']: 'Tudalen flaenorol',
      ['Next Page']: 'Tudalen nesaf',
      ['Last Page']: 'Tudalen olaf',
      ['Search table']: "Chwilio'r tabl",
      ['Show columns']: 'Dangos colofnau',
      ['Export PDF']: 'Allforio PDF',
      ['Export CSV']: 'Allforio CSV',
      ['No records to display']: 'Dim cofnodion i’w dangos',
    },
  },
};

export const USER_NOT_FOUND = `You do not have an active account with us, please email ${
  getProductVariant().email
} for more information.`;
export const USER_NOT_ACTIVATED = `Your account must be activated before you can access ${
  getProductVariant().title
}. Contact your school administrator.`;
export const LOGIN_DISABLED = `Your subscription is no longer active. Please email ${
  getProductVariant().email
} for more information.`;

export const S3_CSV_BUCKET = 'smidprime-csv-uploads';
export const S3_VIDEOS_BUCKET = 'smidprime-support-videos';
export const S3_STUDENT_FILES_BUCKET = 'smidprime-student-files';
export const S3_TEMPLATE_IMAGES_BUCKET = 'smidprime-template-images';
export const S3_CUSTOM_PUPIL_DASH_BUCKET = 'smidprime-custom-pupil-dash';

export const attendanceCodes = [
  {
    Id: '-',
    Mark: '-',
    Description: 'No Mark',
    MeaningDescription: 'No mark',
    ShortMeaningDescription: 'No Mark',
    PhysicalMeaning: 'NOMARK',
  },
  {
    Id: '/',
    Mark: '/',
    Description: 'Present',
    MeaningDescription: 'Present',
    ShortMeaningDescription: 'Present',
    PhysicalMeaning: 'IN',
  },
  {
    Id: '\\',
    Mark: '\\',
    Description: 'Present',
    MeaningDescription: 'Present',
    ShortMeaningDescription: 'Present',
    PhysicalMeaning: 'IN',
  },
  {
    Id: 'B',
    Mark: 'B',
    Description: 'Educated off site (not Dual reg.)',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'C',
    Mark: 'C',
    Description: 'Other authorised circumstances',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'D',
    Mark: 'D',
    variants: [
      {
        Id: 'D',
        Mark: 'D',
        country: 'ENGLAND',
        Description: 'Dual registration',
        MeaningDescription: 'Attendance not required',
        ShortMeaningDescription: 'Not Required',
        PhysicalMeaning: 'OUT',
      },
      {
        Id: 'D',
        Mark: 'D',
        country: 'WALES',
        Description: 'Dual registration',
        MeaningDescription: 'Approved Edu. Activity',
        ShortMeaningDescription: 'Approved',
        PhysicalMeaning: 'OUT',
      },
    ],
  },
  {
    Id: 'E',
    Mark: 'E',
    Description: 'Excluded',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'G',
    Mark: 'G',
    Description: 'Family holiday (Not agreed)',
    MeaningDescription: 'Unauthorised Absence',
    ShortMeaningDescription: 'Unauthorised',
    PhysicalMeaning: 'OUT',
  },
  {
    //now retired for english schools
    Id: 'H',
    Mark: 'H',
    variants: [
      {
        Id: 'H',
        Mark: 'H',
        Description: 'Family holiday (agreed)',
        MeaningDescription: 'Attendance not required',
        ShortMeaningDescription: 'Not Required',
        PhysicalMeaning: 'OUT',
      },
      {
        Id: 'H',
        Mark: 'H',
        country: 'WALES',
        Description: 'Family holiday (agreed)',
        MeaningDescription: 'Authorised Absence',
        ShortMeaningDescription: 'Authorised',
        PhysicalMeaning: 'OUT',
      },
      {
        Id: 'H',
        Mark: 'H',
        year: 2023,
        country: 'ENGLAND',
        Description: 'Family holiday (agreed)',
        MeaningDescription: 'Authorised Absence',
        ShortMeaningDescription: 'Authorised',
        PhysicalMeaning: 'OUT',
      },
    ],
  },
  {
    Id: 'I',
    Mark: 'I',
    Description: 'Illness',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'K',
    Mark: 'K',
    Description:
      'Attending education provision arranged by the local authority',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'L',
    Mark: 'L',
    Description: 'Late (before registers closed)',
    MeaningDescription: 'Present',
    ShortMeaningDescription: 'Present',
    PhysicalMeaning: 'LATE',
  },
  {
    Id: 'M',
    Mark: 'M',
    Description: 'Medical/Dental appointments',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'N',
    Mark: 'N',
    Description: 'No reason yet provided for absence',
    MeaningDescription: 'Unauthorised Absence',
    ShortMeaningDescription: 'Unauthorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'O',
    Mark: 'O',
    Description: 'Unauthorised Abs',
    MeaningDescription: 'Unauthorised Absence',
    ShortMeaningDescription: 'Unauthorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'P',
    Mark: 'P',
    Description: 'Approved sporting activity',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'R',
    Mark: 'R',
    Description: 'Religious observance',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'S',
    Mark: 'S',
    Description: 'Study leave',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'T',
    Mark: 'T',
    Description: 'Traveller absence',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'V',
    Mark: 'V',
    Description: 'Educational visit or trip',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'W',
    Mark: 'W',
    Description: 'Work experience',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: '#',
    Mark: '#',
    Description: 'Planned whole or partial school closure',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'Y',
    Mark: 'Y',
    Description: 'Unable to attend due to exceptional circumstances',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'X',
    Mark: 'X',
    Description: 'Non-compulsory school age absence',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'Z',
    Mark: 'Z',
    Description: 'DfES Z: Pupil not on roll',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'F',
    Mark: 'F',
    Description: 'Extended family holiday (agreed)',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'J',
    Mark: 'J',
    variants: [
      {
        Id: 'J',
        Mark: 'J',
        Description: 'Interview',
        MeaningDescription: 'Approved Edu. Activity',
        ShortMeaningDescription: 'Approved',
        PhysicalMeaning: 'OUT',
      },
      {
        Id: 'J',
        Mark: 'J',
        country: 'ENGLAND',
        year: 2023,
        Description: 'Interview',
        MeaningDescription: 'Approved Edu. Activity',
        ShortMeaningDescription: 'Approved',
        PhysicalMeaning: 'OUT',
      },
      {
        Id: 'J',
        Mark: 'J',
        country: 'ENGLAND',
        Description: 'Interview',
        MeaningDescription: 'Attendance not required',
        ShortMeaningDescription: 'Not Required',
        PhysicalMeaning: 'OUT',
      },
    ],
  },
  {
    Id: 'B1',
    Mark: 'B1',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Pupil in receipt of online learning provided by a local authority',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'B2',
    Mark: 'B2',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Pupil in receipt of online learning provided by the school',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'D1',
    Mark: 'D1',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Dual registered (pupil attending other educational setting)',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'P1',
    Mark: 'P1',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Approved sporting activity',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'P2',
    Mark: 'P2',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Approved performance',
    MeaningDescription: 'Approved Edu. Activity',
    ShortMeaningDescription: 'Approved',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'C3',
    Mark: 'C3',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Pupil who is flexi-schooled',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'C4',
    Mark: 'C4',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Pupil who is on a part-time timetable',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'I1',
    Mark: 'I1',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Physical illness',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'I2',
    Mark: 'I2',
    availableFrom: { country: 'WALES', year: 2026 },
    Description: 'Non-physical illness',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: 'U',
    Mark: 'U',
    Description: 'Late (after registers closed)',
    MeaningDescription: 'Unauthorised Absence',
    ShortMeaningDescription: 'Unauthorised',
    PhysicalMeaning: 'LATE',
  },
  {
    Id: '7',
    Mark: '7',
    Description: 'Illness due to Covid 19',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: '8',
    Mark: '8',
    Description: 'Self-isolating due to Covid 19',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    Id: '9',
    Mark: '9',
    Description: 'Shielding due to Covid 19',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    PhysicalMeaning: 'OUT',
  },
  {
    id: 'A124965801',
    Mark: 'X01',
    Description: 'Non compulsory school age pupil not required to be in school',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X01',
    MeaningDescription: 'Unauthorised Absence',
    ShortMeaningDescription: 'Unauthorised',
  },
  {
    id: 'A493975360',
    Mark: 'X02',
    Description: 'Pupil self-isolating with coronavirus (COVID-19) symptoms',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X02',
  },
  {
    id: 'A795820045',
    Mark: 'X05',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    Description:
      'Pupils required to self-isolate as part of quarantine requirement (after arriving in the UK from a non-exempt country or territory)',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X05',
  },
  {
    id: 'A627303844',
    Mark: 'X06',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    Description:
      'Pupil not in school because they have been advised specifically by their Doctor or public health authority that they are clinically extremely vulnerable and should not attend',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X06',
  },
  {
    id: 'A996587839',
    Mark: 'X07',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    Description:
      'Pupil advised specifically not to attend school as part of restrictions to education set out in Government advice',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X07',
  },
  {
    id: 'A828725974',
    Mark: 'I01',
    Description: 'Illness',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: 'I',
    government_code: 'I01',
  },
  {
    id: 'A928644705',
    Mark: 'I02',
    Description: 'Illness- Confirmed case of coronavirus (COVID-19)',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: 'I',
    government_code: 'I02',
  },
  {
    id: 'A323305093',
    Mark: 'X08',
    Description:
      'Pupil not attending school in line with advice from Directors of Public Health as part of outbreak management',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X08',
  },
  {
    id: 'A700043804',
    Mark: 'X09',
    Description:
      'Pupil or student in a specific group required, by NHS test and trace, to self-isolate as a close contact of a confirmed case of COVID-19 (e.g. those over 18 years 6 months and not fully vaccinated)',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: 'X',
    government_code: 'X09',
  },
  {
    id: 'A742433741',
    Mark: 'C1',
    variants: [
      {
        id: 'A742433741',
        Mark: 'C1',
        country: 'WALES',
        startYear: 2026,
        Description: 'Other authorised circumstances (not covered by another appropriate code or description)',
        MeaningDescription: 'Authorised Absence',
        ShortMeaningDescription: 'Authorised',
        type: 'AUTHORISED',
        parent_code: null,
        government_code: 'C1',
      },
    ],
    Description:
      'Absent with leave for the purpose of participating in a regulated performance',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: null,
    government_code: 'C1',
  },
  {
    id: 'A951720191',
    Mark: 'C2',
    variants: [
      {
        id: 'A951720191',
        Mark: 'C2',
        country: 'WALES',
        startYear: 2026,
        Description: 'Absence due to a paid performance',
        MeaningDescription: 'Authorised Absence',
        ShortMeaningDescription: 'Authorised',
        type: 'AUTHORISED',
        parent_code: null,
        government_code: 'C2',
      },
    ],
    Description:
      'Absent with leave, of compulsory school age and temporary reduced timetable does not require them to attend',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: null,
    government_code: 'C2',
  },
  {
    id: 'A575096676',
    Mark: 'J1',
    Description:
      'Absent with leave for the purpose of attending an interview for employment or for admission to another educational institution',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: null,
    government_code: 'J1',
  },
  {
    id: 'A884822049',
    Mark: 'Y1',
    variants: [
      {
        id: 'A884822049',
        Mark: 'Y1',
        country: 'WALES',
        startYear: 2026,
        Description: 'Partial closure',
        MeaningDescription: 'Attendance not required',
        ShortMeaningDescription: 'Not Required',
        type: 'VOID',
        parent_code: null,
        government_code: 'Y1',
      },
    ],
    Description:
      "Unable to attend because school is not within walking distance of pupil's home and transport to and from school normally provided is not available",
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y1',
  },
  {
    id: 'A1253176760',
    Mark: 'Y2',
    variants: [
      {
        id: 'A1253176760',
        Mark: 'Y2',
        country: 'WALES',
        startYear: 2026,
        Description: 'Whole-school closure',
        MeaningDescription: 'Attendance not required',
        ShortMeaningDescription: 'Not Required',
        type: 'VOID',
        parent_code: null,
        government_code: 'Y2',
      },
    ],
    Description:
      'Unable to attend due to widespread disruption to travel caused by a local, national, or international emergency',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y2',
  },
  {
    id: 'A1085573459',
    Mark: 'Y3',
    Description:
      'Part of the school premises is unavoidably out of use and pupil cannot be accommodated in parts of the premises that remain in use',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y3',
  },
  {
    id: 'A1185622762',
    Mark: 'Y4',
    Description:
      'Whole school closed when school was due to meet for a session, but session has been cancelled',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y4',
  },
  {
    id: 'A1555025541',
    Mark: 'Y5',
    Description:
      'Unable to attend because pupil is subject to a sentence of detention',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y5',
  },
  {
    id: 'A1386373660',
    Mark: 'Y6',
    Description:
      'Travel to or attendance at school would be contrary/prohibited by guidance/law relating to incidence or transmission of infection or disease',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y6',
  },
  {
    id: 'A1755383735',
    Mark: 'Y7',
    MeaningDescription: 'Attendance not required',
    ShortMeaningDescription: 'Not Required',
    Description: 'Unable to attend because of any other unavoidable cause',
    type: 'VOID',
    parent_code: null,
    government_code: 'Y7',
  },
  {
    id: 'A1297912824',
    Mark: ';',
    Description: 'Illness due to Covid 19',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: null,
    government_code: ';',
  },
  {
    id: 'A813359890',
    Mark: '=',
    Description: 'Illness due to Covid 19',
    MeaningDescription: 'Authorised Absence',
    ShortMeaningDescription: 'Authorised',
    type: 'AUTHORISED',
    parent_code: null,
    government_code: '=',
  },
];

export { getCustomAttendanceCodes, getCustomAttendanceCodesForDate, getAttendanceAcademicYear, getAttendanceCodeDateKey, getAttendanceCodeChangeDates, parseAttendanceMarks, getAttendanceSessions };
const attendanceCodesCache = new Map();
export function getAttendanceCodes(country, date, customCodes = []) {
  const year = getAttendanceAcademicYear(date);
  const yearCodes = getCustomAttendanceCodesForDate(customCodes, date);
  if (attendanceCodesCache.has(`${country}:${year}`)) {
    return mergeCustomAttendanceCodes(
      attendanceCodesCache.get(`${country}:${year}`),
      yearCodes
    );
  }
  const codes = attendanceCodes
    .filter((code) => {
      const availableFrom = code.availableFrom;
      if (
        availableFrom &&
        (availableFrom.country !== country || year < availableFrom.year)
      ) {
        return false;
      }
      return true;
    })
    .map((code) => {
    if (code?.variants) {
      const exactMatch = code.variants.find(
        (variant) => variant?.country === country && variant?.year === year
      );

      if (exactMatch) return exactMatch;

      const activeCountryMatch = code.variants.find(
        (variant) =>
          variant?.country === country &&
          variant?.startYear !== undefined &&
          year >= variant.startYear
      );
      if (activeCountryMatch) return activeCountryMatch;

      const countryMatches = code.variants.filter(
        (variant) => variant?.country === country
      );
      const yearMatches = code.variants.filter(
        (variant) => variant?.year === year
      );

      if (countryMatches.length && !yearMatches.length) {
        const countryMatch = countryMatches.find((variant) => !variant?.year);
        if (countryMatch) return countryMatch;
      }

      if (!countryMatches.length && yearMatches.length) {
        const yearMatch = yearMatches.find((variant) => !variant?.country);
        if (yearMatch) return yearMatch;
      }

      return (
        code.variants.find(
          (variant) => !variant?.year && !variant?.country
        ) || code
      );
    }

    return code;
    });
  attendanceCodesCache.set(`${country}:${year}`, codes);
  return mergeCustomAttendanceCodes(codes, yearCodes);
}

export const defaultAoleSystem = {
  id: null,
  name: 'Default AOLE System',
  options: [
    {
      name: '1',
      precedence: 3,
      color: '#157002',
    },
    {
      name: '2',
      precedence: 2,
      color: '#f29705',
    },
    {
      name: '3',
      precedence: 1,
      color: '#cf1b0e',
    },
  ],
};

export const teacherActions = [
  {
    actionType: 'ATTENDANCELETTER',
    action: 'Attendance Letter Sent Home',
  },
  {
    actionType: 'ATTENDANCESPOKE',
    action: 'Spoke to Parents about Attendance',
  },
  {
    actionType: 'FAMILYCOMMUNICATION',
    action: 'Communication with families',
  },
  {
    actionType: 'AGENCYCOMMUNICATION',
    action: 'Communication with outside agencies',
  },
];
